import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw } from "lucide-react";
import { useSEO } from "../lib/seo";
import { NotificationSettings } from "../components/NotificationSettings";
import { CaseStudiesAdmin } from "../components/CaseStudiesAdmin";
import { ContentEditor } from "../components/ContentEditor";
import { AboutAdmin } from "../components/AboutAdmin";
import { ServicesAdmin } from "../components/ServicesAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "qualified", "won", "archived"];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const LeadCard = ({ lead, onStatus }) => (
  <div
    data-testid={`lead-card-${lead.id}`}
    className="border border-white/10 bg-panel p-6 sm:p-7"
  >
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p className="font-display text-lg font-semibold text-white" data-testid={`lead-name-${lead.id}`}>
          {lead.name}
        </p>
        <p className="mt-1 text-sm text-gold">{lead.email}</p>
      </div>
      <label className="flex items-center gap-2 text-xs">
        <span className="font-mono uppercase tracking-[0.2em] text-white/50">Status</span>
        <select
          data-testid={`lead-status-select-${lead.id}`}
          value={lead.status || "new"}
          onChange={(e) => onStatus(lead.id, e.target.value)}
          className="rounded-full border border-white/20 bg-surface px-4 py-2 text-sm text-white outline-none transition-colors focus:border-gold"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s} className="bg-panel">
              {s}
            </option>
          ))}
        </select>
      </label>
    </div>
    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[11px] uppercase tracking-[0.15em] text-white/50">
      {lead.company && <span>{lead.company}</span>}
      {lead.industry && <span>{lead.industry}</span>}
      {lead.budget && <span>{lead.budget}</span>}
      <span>{fmtDate(lead.created_at)}</span>
    </div>
    {lead.services?.length > 0 && (
      <div className="mt-4 flex flex-wrap gap-2">
        {lead.services.map((s) => (
          <span
            key={s}
            className="rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-xs text-gold"
          >
            {s}
          </span>
        ))}
      </div>
    )}
    <p className="mt-4 border-t border-white/10 pt-4 text-sm leading-relaxed text-white/80">
      {lead.message}
    </p>
  </div>
);

export default function Admin() {
  useSEO("Lead Dashboard — YUXEL", "Internal lead dashboard.");
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [code, setCode] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/leads`, { credentials: "include" });
      if (res.status === 401) {
        setAuthed(false);
        return;
      }
      if (!res.ok) throw new Error("failed");
      setLeads(await res.json());
      setAuthed(true);
    } catch {
      toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/admin/me`, { credentials: "include" });
        setAuthed(r.ok);
      } catch {
        setAuthed(false);
      }
      setChecking(false);
    })();
  }, []);

  useEffect(() => {
    if (authed) loadLeads();
  }, [authed, loadLeads]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => null);
        throw new Error(formatApiErrorDetail(data?.detail));
      }
      setAuthed(true);
      setCode("");
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err.message || "Access denied.");
    }
  };

  const logout = async () => {
    await fetch(`${API}/admin/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    setAuthed(false);
    setLeads([]);
  };

  const setStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      const r = await fetch(`${API}/leads/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error("failed");
    } catch {
      toast.error("Could not update status.");
      loadLeads();
    }
  };

  const counts = STATUSES.reduce(
    (acc, s) => ({ ...acc, [s]: leads.filter((l) => (l.status || "new") === s).length }),
    {}
  );

  if (checking) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-20">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-mist">Checking access…</p>
      </div>
    );
  }

  if (!authed) {
    return (
      <section className="flex min-h-[80vh] items-center justify-center px-5 pb-20 pt-32" aria-label="Admin login">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-white/10 bg-panel p-8 sm:p-10"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gold/15 text-gold">
            <Lock className="h-5 w-5" />
          </span>
          <h1 className="mt-6 font-serif text-3xl font-medium text-white">Lead dashboard</h1>
          <p className="mt-3 text-sm leading-relaxed text-mist">
            Internal access only. Enter the team access code to view consultation requests.
          </p>
          <label htmlFor="admin-code" className="mb-2 mt-8 block font-mono text-xs uppercase tracking-[0.2em] text-white/60">
            Access code
          </label>
          <input
            id="admin-code"
            type="password"
            data-testid="admin-code-input"
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full rounded-xl border border-white/15 bg-surface px-4 py-3.5 text-base text-white outline-none transition-colors focus:border-gold"
            placeholder="••••••••••"
          />
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-6 w-full rounded-full bg-gold py-3.5 font-semibold text-ink transition-all duration-300 hover:bg-champagne"
          >
            Unlock dashboard
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-5 pb-24 pt-32 sm:px-8 sm:pb-32" aria-label="Lead dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Internal</p>
          <h1 className="mt-3 font-serif text-4xl font-medium text-white sm:text-5xl">
            Consultation requests
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="admin-refresh-button"
            onClick={loadLeads}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-red-400 hover:text-red-400"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="lead-status-counts">
        {STATUSES.map((s) => (
          <span
            key={s}
            className="rounded-full border border-white/15 bg-surface px-4 py-1.5 font-mono text-xs uppercase tracking-[0.15em] text-white/70"
          >
            {s} <span className="text-gold">{counts[s]}</span>
          </span>
        ))}
      </div>

      <AboutAdmin />

      <ServicesAdmin />

      <CaseStudiesAdmin />

      <ContentEditor />

      <NotificationSettings />

      <div className="mt-10 space-y-5">
        {leads.length === 0 && !loading && (
          <p data-testid="leads-empty-state" className="border border-white/10 bg-panel p-10 text-center text-sm text-mist">
            No consultation requests yet. Submissions from the contact page appear here.
          </p>
        )}
        {leads.map((l) => (
          <LeadCard key={l.id} lead={l} onStatus={setStatus} />
        ))}
      </div>
    </section>
  );
}
